import { type MyCompanyListItem, EVENTS } from '@tandemone/types'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar'
import {
  type SwitchTarget,
  useDirtyForms,
  useOpenAddCompanySheet,
  useSwitchCompany
} from '@/domains/companies'
import { reportError, track } from '@/domains/observability'
import { CompanyLogo, useSetupState } from '@/domains/setup'

export function CompanySwitcher({
  companies,
  canAddCompany,
  showSuperScope
}: {
  companies: MyCompanyListItem[]
  canAddCompany: boolean
  showSuperScope: boolean
}) {
  const { isMobile, setOpenMobile } = useSidebar()
  const { setupState } = useSetupState()
  const dirtyForms = useDirtyForms()
  const openAddCompanySheet = useOpenAddCompanySheet()
  const [switchError, setSwitchError] = useState<string | null>(null)
  /** the scope we're about to switch to, captured at click-time, so
   *  the AlertDialog confirm button knows the target without storing
   *  separate state. null means no confirm in flight. */
  const [pendingTarget, setPendingTarget] = useState<SwitchTarget | null>(null)
  const switchMutation = useSwitchCompany()

  const inSuperScope = setupState?.scope === 'super'
  const activeCompanyId = setupState?.companyId ?? null
  /** no companies[0] fallback: in Super scope none of them is active, and
   *  falling back would label the trigger with an arbitrary company. */
  const active = inSuperScope
    ? null
    : (companies.find(company => company.id === activeCompanyId) ?? null)

  async function runSwitch(target: SwitchTarget): Promise<void> {
    setSwitchError(null)
    /* the switch re-renders in place, so the phone drawer never unmounts. */
    setOpenMobile(false)
    try {
      await switchMutation.mutateAsync(target)
    } catch (failure) {
      reportError(failure)
      setSwitchError(extractErrorMessage(failure))
    }
  }

  function handleSelect(target: SwitchTarget): void {
    if (target.scope === 'super' && inSuperScope) return
    if (target.scope === 'company' && !inSuperScope) {
      if (target.companyId === activeCompanyId) return
    }
    /** dirty-form gate: if any form is mid-edit, prompt before
     *  re-scoping. forms register themselves with the dirty registry
     *  (see useRegisterDirtyForm). check at click-time, not in render,
     *  to avoid subscribing to every keystroke. */
    if (dirtyForms.hasDirty()) {
      setPendingTarget(target)
      return
    }
    void runSwitch(target)
  }

  function handleConfirmSwitch(): void {
    if (!pendingTarget) return
    const target = pendingTarget
    setPendingTarget(null)
    void runSwitch(target)
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size='lg'
                className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              >
                <CompanyLogo
                  logoId={
                    inSuperScope ? null : (setupState?.companyLogoId ?? null)
                  }
                  name={
                    inSuperScope
                      ? 'TandemOne'
                      : (active?.name ?? setupState?.companyName ?? 'TandemOne')
                  }
                />
                <div className='grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden'>
                  <span className='truncate font-semibold'>
                    {inSuperScope
                      ? 'Super'
                      : (active?.name ??
                        setupState?.companyName ??
                        'TandemOne')}
                  </span>
                  <span className='truncate text-xs text-sidebar-foreground/60'>
                    {inSuperScope ? 'TandemOne staff' : 'Powered by TandemOne'}
                  </span>
                </div>
                <ChevronsUpDown className='ml-auto size-4 text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden' />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-(--radix-dropdown-menu-trigger-width) min-w-64 rounded-lg'
              align='start'
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
              onCloseAutoFocus={event => event.preventDefault()}
            >
              {showSuperScope ? (
                <>
                  <DropdownMenuItem
                    onClick={() => handleSelect({ scope: 'super' })}
                    className='cursor-pointer gap-2 p-2'
                  >
                    <CompanyLogo logoId={null} name='TandemOne' />
                    <span className='flex-1 truncate font-medium'>Super</span>
                    {inSuperScope ? (
                      <Check className='size-4 text-accent dark:text-accent-mid' />
                    ) : null}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuLabel className='text-xs text-muted-foreground'>
                Companies
              </DropdownMenuLabel>
              {companies.map(company => {
                const isActive = !inSuperScope && company.id === activeCompanyId
                return (
                  <DropdownMenuItem
                    key={company.id}
                    onClick={() =>
                      handleSelect({ scope: 'company', companyId: company.id })
                    }
                    className='cursor-pointer gap-2 p-2'
                  >
                    <div className='flex size-6 shrink-0 items-center justify-center rounded-md border'>
                      <span className='text-[10px] font-semibold uppercase'>
                        {company.name.charAt(0)}
                      </span>
                    </div>
                    <span className='flex-1 truncate'>{company.name}</span>
                    {isActive ? (
                      <Check className='size-4 text-accent dark:text-accent-mid' />
                    ) : null}
                  </DropdownMenuItem>
                )
              })}
              {canAddCompany ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => {
                      track(EVENTS.ADD_COMPANY_SHEET_OPENED, {})
                      /* the sheet renders under the phone drawer otherwise. */
                      setOpenMobile(false)
                      openAddCompanySheet()
                    }}
                    className='cursor-pointer gap-2 p-2'
                  >
                    <div className='flex size-6 shrink-0 items-center justify-center rounded-md border bg-transparent'>
                      <Plus className='size-4' />
                    </div>
                    <span className='font-medium text-muted-foreground'>
                      Add Company
                    </span>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
        {switchError ? (
          <SidebarMenuItem>
            <p className='px-2 text-xs text-red-600'>{switchError}</p>
          </SidebarMenuItem>
        ) : null}
      </SidebarMenu>

      <AlertDialog
        open={pendingTarget !== null}
        onOpenChange={open => {
          if (!open) setPendingTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have a form open with unsaved changes. Switching companies now
              will leave them behind. Continue anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSwitch}>
              Switch anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
